import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from '@firebase/auth'
import { useMutation } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import {
  FieldValues,
  useForm,
  UseFormRegisterReturn,
  UseFormSetFocus,
} from 'react-hook-form'
import { UserContext } from '../context/UserContext'
import { auth } from '../firebase'
import { createNewUserInDatabase } from '../services/createNewUserInDatabase'
import API from '../services'
import { Analytics } from '../services/analytics'
import { useNavigate } from 'react-router-dom'

export type AuthValues = {
  name: string
  email: string
  password: string
  passwordRepeat: string
  agreedToPolicies: boolean
}

export type AuthMode =
  | 'login'
  | 'register'
  | 'reset'
  | 'verify'
  | 'complete'
  | 'resetSent'
  | 'resetEnterNewPassword'

// export type AuthComponentProps = {
//   values: AuthValues
//   error: string | null
//   isLoading: boolean
//   onSubmit: (data: UseFormHandleSubmit<FieldValues>) => void
//   onChange: (values: AuthValues) => void
//   onModeSwitch: (mode: AuthMode) => void
// }

export type AuthForm = {
  isLoading: boolean
  error: string | null
  name: UseFormRegisterReturn<'name'>
  email: UseFormRegisterReturn<'email'>
  password: UseFormRegisterReturn<'password'>
  passwordRepeat: UseFormRegisterReturn<'passwordRepeat'>
  agreedToPolicies: UseFormRegisterReturn<'agreedToPolicies'>
  setFocus: UseFormSetFocus<FieldValues>
  handleSubmit: () => {}
  onModeSwitch: (mode: AuthMode) => void
}

type Props = {
  defaultMode?: AuthMode | null
  actionCode?: string | null
  introMode?: 'create-gym' | 'join-gym'
}

const useAuth = ({ defaultMode, actionCode, introMode }: Props) => {
  const { user } = useContext(UserContext)
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors },
  } = useForm()

  const fields = {
    name: {
      ...register('name', { maxLength: 40, onChange: () => setError(null) }),
    },
    email: {
      ...register('email', { maxLength: 40, onChange: () => setError(null) }),
    },
    password: {
      ...register('password', {
        maxLength: 40,
        onChange: () => setError(null),
      }),
    },
    passwordRepeat: {
      ...register('passwordRepeat', {
        maxLength: 40,
        onChange: () => setError(null),
      }),
    },
    agreedToPolicies: {
      ...register('agreedToPolicies'),
    },
  }

  const [mode, setMode] = useState<AuthMode>(
    defaultMode ? defaultMode : !auth.currentUser ? 'register' : 'verify'
  ) // we check if email verified before showing auth module
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isSuccess, setSuccess] = useState<boolean>(false)

  const registerMutation = useMutation({
    mutationFn: async (data: {
      name: string
      email: string
      password: string
    }) => {
      const res = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      )
      await sendEmailVerification(res.user)
      await createNewUserInDatabase(data.name, introMode)
      Analytics._logEvent({ name: 'auth_register', params: {} })
      return res.user
    },
    onError: (err) => {
      setError(translateAuthErrors(JSON.stringify(err)))
    },
  })

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const result = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      )
      Analytics._logEvent({ name: 'auth_login', params: {} })
      return result
    },
    onError: (err) => {
      setError(translateAuthErrors(JSON.stringify(err)))
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      await sendPasswordResetEmail(auth, email)
      return true
    },
    onSuccess: () => {
      setMode('resetSent')
    },
    onError: (err) => {
      if (JSON.stringify(err).includes('user-not-found')) {
        setError(null)
        setMode('resetSent')
        return
      }
      setError(translateAuthErrors(JSON.stringify(err)))
    },
  })

  const confirmResetMutation = useMutation({
    mutationFn: async (newPassword: string) => {
      if (!actionCode) throw new Error('No action code provided')
      return await confirmPasswordReset(auth, actionCode, newPassword)
    },
    onSuccess: () => {
      Analytics._logEvent({ name: 'auth_reset', params: {} })
    },
    onError: (err) => {
      setError(translateAuthErrors(JSON.stringify(err)))
    },
  })

  const verifyEmailMutation = useMutation({
    mutationFn: async () => {
      if (!actionCode) throw new Error('No action code provided')
      await applyActionCode(auth, actionCode)
      await auth.currentUser?.reload()
    },
    onSuccess: () => {
      navigate('/')
      Analytics._logEvent({ name: 'auth_verify_email', params: {} })
    },
    onError: (err) => {
      console.error('Error verifying email:', err)
      setError(JSON.stringify(err))
    },
  })

  const recoverEmailMutation = useMutation({
    mutationFn: async () => {
      if (!actionCode) throw new Error('No action code provided')
      const info = await checkActionCode(auth, actionCode)
      const restoredEmail = info['data']['email']
      await applyActionCode(auth, actionCode)

      if (typeof restoredEmail !== 'string' || !restoredEmail) {
        throw new Error('Invalid restored email')
      }

      await API.patchUserAttribute({ email: restoredEmail })
      return restoredEmail
    },
    onSuccess: () => {
      console.log('Email recovered')
      navigate('/')
    },
  })

  const onSubmit = async (data: any) => {
    const error = checkForErrors(data, mode)
    if (error) {
      setError(error)
      return
    }

    setError(null)
    setLoading(true)
    setSuccess(false)

    try {
      switch (mode) {
        case 'register':
          await registerMutation.mutateAsync(data)
          break
        case 'login':
          await loginMutation.mutateAsync(data)
          break
        case 'reset':
          await resetPasswordMutation.mutateAsync(data.email)
          break
        case 'resetEnterNewPassword':
          await confirmResetMutation.mutateAsync(data.password)
          break
      }

      setSuccess(true)
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyEmail = () => verifyEmailMutation.mutate()
  const handleRecoverEmail = () => recoverEmailMutation.mutate()

  const clearErrors = () => {
    setError(null)
  }

  return {
    authForm: {
      isLoading:
        isLoading ||
        registerMutation.isPending ||
        loginMutation.isPending ||
        resetPasswordMutation.isPending ||
        confirmResetMutation.isPending,
      error,
      name: fields.name,
      email: fields.email,
      password: fields.password,
      passwordRepeat: fields.passwordRepeat,
      agreedToPolicies: fields.agreedToPolicies,
      handleSubmit: handleSubmit(onSubmit),
      setFocus,
    },
    mode,
    setMode,
    setFocus,
    handleVerifyEmail,
    handleRecoverEmail,
    clearErrors,
    clearFields: reset,
    isLoading,
    isSuccess,
    error,
  }
}

export default useAuth

const checkForErrors = (values: AuthValues, mode: AuthMode) => {
  // Registration errors have slighly different wording
  if (mode === 'register') {
    if (!values.email && !values.password) {
      return translateAuthErrors('no-email-and-password-first')
    }
    if (!values.password) {
      return translateAuthErrors('no-password-first')
    }
    if (!values.name) {
      return translateAuthErrors('no-name')
    }

    if (!values.email) {
      return translateAuthErrors('no-email')
    }

    if (!values.agreedToPolicies)
      return 'You must agree with Terms of Use and Privacy Policy to create an account'
  }

  if (mode === 'login') {
    if (!values.email && !values.password) {
      return translateAuthErrors('no-email-and-password')
    }
    if (!values.password) {
      return translateAuthErrors('no-password')
    }

    if (!values.email) {
      return translateAuthErrors('no-email')
    }
  }

  if (mode === 'reset') {
    if (!values.email) return translateAuthErrors('no-email')
  }

  if (mode === 'resetEnterNewPassword') {
    if (!values.password) {
      return translateAuthErrors('enter-new-passwords')
    }
  }
}

export const translateAuthErrors = (err: string, from?: string) => {
  if (from === 'reset' && err.includes('user-not-found')) return 'Donno, man...'

  if (from === 'settings' && err.includes('wrong-password'))
    return 'Incorrect password'

  if (err.includes('user-not-found') || err.includes('wrong-password'))
    return 'Incorrect e-mail or password'

  if (err.includes('invalid-email')) return 'Please enter a valid e-mail'
  if (err.includes('email-already-in-use'))
    return 'This e-mail is already in use'

  if (err.includes('no-email-and-password-first'))
    return 'Please enter your e-mail and create a password'

  if (err.includes('no-email-and-password'))
    return 'Please enter your e-mail and the password'

  if (err.includes('no-email')) return 'Please enter your e-mail'

  if (err.includes('no-password-first')) return 'Please create a password'

  if (err.includes('no-password')) return 'Please enter the password'
  if (err.includes('auth/weak-password'))
    return 'Password should be at least 6 characters'

  if (err.includes('no-name')) return 'Please enter your name'
  if (err.includes('network-request-failed'))
    return "There's a problem with the network connection"

  if (err.includes('popup-closed-by-user')) return '' // google sign-in closed
  if (err.includes('invalid-action-code'))
    return 'Password reset link is no longer valid. Please request a new link' // google sign-in closed

  if (err === 'enter-new-passwords') return 'Please enter new password'

  // If all else fails
  return err.replace('Firebase: ', '')
}
